import {
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

export interface OpportunityHubFoldToggleProps {
  /** Every group is collapsed, so the fold half reads as active. */
  isFolded: boolean;
  /** Every group is expanded, so the unfold half reads as active. */
  isUnfolded: boolean;
  onFoldAll: () => void;
  onUnfoldAll: () => void;
}

/** Takes the slot the legacy density toggle holds — here it folds and unfolds every group. */
export function OpportunityHubFoldToggle({
  isFolded,
  isUnfolded,
  onFoldAll,
  onUnfoldAll,
}: OpportunityHubFoldToggleProps) {
  const { t } = useTranslation();

  const options = [
    {
      key: "fold",
      icon: "fold-vertical" as const,
      isActive: isFolded,
      onClick: onFoldAll,
      label: t("personaOpportunities.hub.groupBy.foldAll", "Fold all groups"),
    },
    {
      key: "unfold",
      icon: "unfold-vertical" as const,
      isActive: isUnfolded,
      onClick: onUnfoldAll,
      label: t("personaOpportunities.hub.groupBy.unfoldAll", "Unfold all groups"),
    },
  ];

  return (
    <TooltipProvider delayDuration={400}>
      <div className="inline-flex items-center rounded-md border border-primary/30 shadow-xs dark:border-primary/40">
        {options.map((option) => (
          <Tooltip key={option.key}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={option.onClick}
                aria-label={option.label}
                className={cn(
                  "inline-flex min-h-11 min-w-11 items-center justify-center text-sm transition-colors first:rounded-l-md last:rounded-r-md [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8",
                  option.isActive
                    ? "bg-primary/10 text-primary dark:bg-primary/20"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                )}
              >
                <Icon name={option.icon} size="xs" color="inherit" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{option.label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
